import React, { useRef, useEffect, useMemo } from 'react';
import { useLivePreviewStore } from '@store/livePreviewStore';
import { PostMessageService } from '@renderer/services/slot-core/PostMessageService';

interface Props {
  className?: string;
}

export const IframeGameViewport: React.FC<Props> = ({ className }) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const viewport = useLivePreviewStore(s => s.viewport);

  const gameWidth = viewport.customWidth ?? 720;
  const gameHeight = viewport.customHeight ?? 1280;

  // Calculate scale to fit the container
  const [containerSize, setContainerSize] = React.useState({ width: 400, height: 600 });

  useEffect(() => {
    if (!containerRef.current) return;
    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry) {
        setContainerSize({
          width: entry.contentRect.width,
          height: entry.contentRect.height,
        });
      }
    });
    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  const displayScale = useMemo(() => {
    const scaleX = containerSize.width / gameWidth;
    const scaleY = containerSize.height / gameHeight;
    return Math.min(scaleX, scaleY, 1);
  }, [containerSize, gameWidth, gameHeight]);

  // Register iframe for postMessage communication
  useEffect(() => {
    const iframe = iframeRef.current;
    if (iframe) {
      PostMessageService.setIframe(iframe);
    }
    return () => PostMessageService.setIframe(null);
  }, []);

  // Listen for game state updates
  useEffect(() => {
    const unlisten = PostMessageService.listen((data) => {
      const msg = data as Record<string, unknown>;
      if (msg.type === 'GAME_STATE_UPDATE') {
        // Could update a status display
      }
      if (msg.type === 'SCREENSHOT_RESULT' && msg.dataUrl) {
        // Handle screenshot
      }
    });
    return unlisten;
  }, []);

  return (
    <div
      ref={containerRef}
      className={`relative flex items-center justify-center overflow-hidden bg-black ${className ?? ''}`}
      style={{ width: '100%', height: '100%' }}
    >
      <iframe
        ref={iframeRef}
        src="http://localhost:8080/?demo=true"
        style={{
          width: gameWidth,
          height: gameHeight,
          transform: `scale(${displayScale})`,
          transformOrigin: 'center center',
          border: 'none',
        }}
        sandbox="allow-scripts allow-same-origin"
        title="Slot Game Preview"
      />
    </div>
  );
};
